import cn from "classnames";
import {Localize} from "@dp-instant/i18n";
import {useState, useEffect} from "preact/hooks";
import {apiClient} from "@game/api/ApiClient";
import {Images} from "@game/ui/images";
import {DailyLeaderboardResponse, LeaderboardEntry} from "../../../packages/shared/src/index";
import "./ServerLeaderBoard.less";

const MEDAL = ["\u{1F947}", "\u{1F948}", "\u{1F949}"];

function formatCountdown(resetAt: string): string {
    const diff = new Date(resetAt).getTime() - Date.now();
    if (diff <= 0) return "00:00:00";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const ServerLeaderBoard = () => {
    const [data, setData] = useState<DailyLeaderboardResponse | null>(null);
    const [countdown, setCountdown] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiClient.getDailyLeaderboard().then(d => {
            setData(d);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!data?.resetAt) return;
        const tick = () => setCountdown(formatCountdown(data.resetAt));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [data?.resetAt]);

    if (loading) {
        return <div className="dlb__loading">{Localize.get("ui-loading", "Loading...")}</div>;
    }
    if (!data) {
        return <div className="dlb__loading">{Localize.get("ui-error", "Error")}</div>;
    }

    const totalReward = data.rewards.reduce((a, b) => a + b, 0);

    return (
        <div className="dlb">
            <div className="dlb__header">
                <div className="dlb__title">{Localize.get("ui-daily-lb-title", "Daily Rating")}</div>
                <div className="dlb__subtitle">
                    {Localize.get("ui-daily-lb-top", `Top ${data.rewards.length}`)} {Localize.get("ui-daily-lb-win", "win")} {totalReward} <Images.Star /> {Localize.get("ui-daily-lb-everyday", "every day")}
                </div>
                <div className="dlb__timer">{countdown}</div>
            </div>

            <div className="dlb__hint">
                {Localize.get("ui-daily-lb-info", "Win = points. Streak bonus. Premium = x2. Rewards given automatically")}
            </div>

            <div className="dlb__list">
                {data.entries.length === 0 && (
                    <div className="dlb__empty">{Localize.get("ui-daily-lb-empty", "No entries yet. Play to compete!")}</div>
                )}
                {data.entries.map((entry) => (
                    <LeaderboardRow
                        key={entry.userId}
                        entry={entry}
                        isMe={data.myRank === entry.rank}
                        reward={entry.rank <= data.rewards.length ? data.rewards[entry.rank - 1] : undefined}
                    />
                ))}
            </div>

            {data.myRank && data.myRank > data.entries.length && (
                <div className="dlb__my-sticky">
                    #{data.myRank} &middot; {data.myScore} {Localize.get("ui-daily-lb-pts", "pts")}
                </div>
            )}
        </div>
    );
};

type RowProps = {
    entry: LeaderboardEntry;
    isMe: boolean;
    reward?: number;
};

const LeaderboardRow = ({entry, isMe, reward}: RowProps) => {
    return (
        <div className={cn("dlb__row", {"dlb__row--me": isMe})}>
            <div className="dlb__rank">
                {entry.rank <= 3 ? MEDAL[entry.rank - 1] : entry.rank}
            </div>
            <div className="dlb__avatar">
                {entry.photoUrl ? (
                    <img src={entry.photoUrl} alt="" />
                ) : (
                    <div className="dlb__avatar-ph">{entry.firstName.charAt(0)}</div>
                )}
            </div>
            <div className="dlb__info-col">
                <div className="dlb__name">
                    {entry.isPremium && <span className="dlb__premium-badge"><Images.Star /></span>}
                    {entry.firstName}
                </div>
                <div className="dlb__score">
                    {entry.score} {Localize.get("ui-daily-lb-pts", "pts")}
                    {(entry.streak ?? 0) > 0 && (
                        <span className="dlb__streak"> {"🔥"}{entry.streak}</span>
                    )}
                </div>
            </div>
            {reward !== undefined && (
                <div className="dlb__prize">
                    {reward}<Images.Star />
                </div>
            )}
        </div>
    );
};
